import { IGlassCustomization } from "./Customization"
import { IGlass } from "./Glass"
import { ProductType } from "./Product"
import { ISensor } from "./Sensor"
import { TranslateFunction } from "./Common"
import { Glass } from "../components/Glass"

export interface ILayers {
  layersArr: Array<LayerType>
  totalThicknes: number
}

export type LayerType = {
  name: string
  thickness: number
  id: string
  order: number
}

export enum GlassOrder {
  Front,
  Middle,
  Rear,
}

export enum GlassOrderString {
  Front = "Front glass",
  Middle = "Middle glass",
  Rear = "Rear glass",
}

export const GlassOrderStrings = (
  type: ProductType | undefined,
  translated?: boolean,
  t?: TranslateFunction
): Array<string> => {
  if (translated === undefined || translated === false || t === undefined) {
    if (type === undefined) return [GlassOrderString.Front]
    if (type !== ProductType.FortouchO) return [GlassOrderString.Front]
    return [
      GlassOrderString.Front,
      GlassOrderString.Middle,
      GlassOrderString.Rear,
    ]
  } else {
    if (type === undefined) return [t("glass_order_front")]
    if (type !== ProductType.FortouchO) return [t("glass_order_front")]
    return [
      t("glass_order_front"),
      t("glass_order_middle"),
      t("glass_order_rear"),
    ]
  }
}

export const stringToGlassOrder = (s: string): GlassOrder => {
  if (s === GlassOrderString.Rear) return GlassOrder.Rear
  if (s === GlassOrderString.Middle) return GlassOrder.Middle
  return GlassOrder.Front
}

export function generateLayers(
  layers: ILayers,
  glass: IGlass,
  sensor: ISensor,
  customization: IGlassCustomization,
  productType: ProductType
): ILayers {
  /**
   * DEFAULT ORDERING::
   * sensor: 0
   * glass front: 1
   * extra layers: 2
   * glass middle: 3
   * glass rear: 4
   *
   */
  const isGlassNonZeroDim = [
    GlassOrder.Front,
    GlassOrder.Middle,
    GlassOrder.Rear,
  ].map(
    (i: GlassOrder) =>
      Number(glass.height[i]) !== 0 && Number(glass.width[i]) !== 0
  )
  const orderOg: Array<{ key: string; order: number }> = []

  if (layers?.layersArr !== undefined) {
    console.log("LAYERS starting generation with", layers.layersArr)
    layers.layersArr.forEach((l) => {
      orderOg.push({
        key: l.id,
        order: l.order,
      })
    })
  }

  const newLayers: Array<LayerType> = []
  if (glass.withGlass) {
    if (productType === ProductType.FortouchO) {
      if (isGlassNonZeroDim[GlassOrder.Front])
        newLayers.push({
          name: "Front glass",
          id: "G0",
          order: orderOgGet(orderOg, "G0", 1),
          thickness: glass.coverGlass[0],
        })
      if (isGlassNonZeroDim[GlassOrder.Middle])
        newLayers.push({
          name: "Middle glass",
          id: "G1",
          order: orderOgGet(orderOg, "G1", 3),
          thickness: glass.coverGlass[1],
        })
      if (isGlassNonZeroDim[GlassOrder.Rear])
        newLayers.push({
          name: "Rear glass",
          id: "G2",
          order: orderOgGet(orderOg, "G2", 4),
          thickness: glass.coverGlass[2],
        })
    } else {
      if (isGlassNonZeroDim[GlassOrder.Front])
        newLayers.push({
          name: "Front glass",
          id: "G0",
          order: orderOgGet(orderOg, "G0", 1),
          thickness: glass.coverGlass[0],
        })
    }

    if (
      customization.extraLayers !== 0 &&
      (customization.otherSettings.IR ||
        customization.otherSettings.UV ||
        customization.otherSettings.PF)
    ) {
      newLayers.push({
        name: "Protective layers",
        id: "EL",
        order: orderOgGet(orderOg, "EL", 2),
        thickness: customization.extraLayers,
      })
    }
  }

  if (sensor.withSensor) {
    newLayers.push({
      name: "Sensor",
      id: "SN",
      order: orderOgGet(orderOg, "SN", 0),
      thickness: sensor.thickness,
    })
  }

  return {
    layersArr: newLayers
      .sort((l1, l2) => l1.order - l2.order)
      .map((l, i) => {
        return { ...l, order: i }
      }),
    totalThicknes: newLayers.reduce(
      (sum_, layer) => (sum_ += layer.thickness),
      0
    ),
  }
}

function orderOgGet(
  orderOg: Array<{ key: string; order: number }>,
  id: string,
  defaultValue: number
): number {
  const ordeGot = orderOg.find((l) => l.key === id)?.order
  if (ordeGot === undefined) return defaultValue
  return ordeGot
}

export const getFirstReferenceGlass = (
  layersArr: Array<LayerType>,
  front: Glass | undefined,
  middle: Glass | undefined,
  rear: Glass | undefined
): Glass | undefined => {
  const l = layersArr.find(
    (l) => l.id === "G0" || l.id === "G1" || l.id === "G2"
  )

  if (l === undefined) return undefined
  if (l.id === "G0") return front
  if (l.id === "G1") return middle
  return rear
}
